<template>
  <Teleport to="body">
    <div v-if="isVisible" :class="$style.modalOverlay" @click="closeModal" @touchmove.prevent @wheel.prevent>
      <div :class="$style.modalContent" @click.stop>
        <div :class="$style.modalHeader">
          <h2 :class="$style.modalTitle">공연장 이미지 편집</h2>
          <button :class="$style.closeButton" @click="closeModal">✕</button>
        </div>

        <div :class="$style.modalBody">
          <!-- 현재 공연장 이미지 미리보기 -->
          <div :class="$style.imagePreviewContainer">
            <div :class="$style.imagePreview">
              <button @click="triggerFileSelect" :disabled="uploading">
                <img v-if="currentImage || previewImage" :src="previewImage || currentImage" :alt="venueName"
                  :class="$style.previewImage" />
                <div v-else :class="$style.noImage">
                  <span>🏢</span>
                </div>
              </button>
            </div>
            <p :class="$style.imageLabel">{{ venueName }}</p>
          </div>

          <!-- 파일 선택 영역 -->
          <div :class="$style.uploadSection">
            <input ref="fileInput" type="file" accept="image/*" @change="handleFileSelect"
              :class="$style.hiddenInput" />

            <p :class="$style.uploadHint">JPG, PNG 파일만 가능 (최대 5MB)</p>
          </div>

          <!-- 업로드 진행 상태 -->
          <div v-if="uploading" :class="$style.uploadProgress">
            <div :class="$style.spinner"></div>
            <p>업로드 중...</p>
          </div>
        </div>

        <div :class="$style.modalFooter">
          <button v-if="currentImage && !selectedFile" :class="$style.deleteButton" @click="handleDelete"
            :disabled="uploading">
            삭제
          </button>
          <button :class="$style.saveButton" @click="handleUpload" :disabled="uploading || !selectedFile">
            저장
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { uploadVenueImage, deleteImage } from "@/utils/write/storage";
import { setVenueImage, removeVenueImage } from "@/utils/write/venue";

export default {
  name: "VenueImageModal",
  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
    currentImage: {
      type: String,
      default: null,
    },
    venueName: {
      type: String,
      required: true,
    },
  },
  emits: ["close", "image-updated", "image-removed"],
  data() {
    return {
      selectedFile: null,
      previewImage: null,
      uploading: false,
    };
  },
  watch: {
    isVisible(newVal) {
      if (newVal) {
        // 모달이 열릴 때 body 스크롤 막기
        document.body.style.overflow = "hidden";
      } else {
        // 모달이 닫힐 때 body 스크롤 복원
        document.body.style.overflow = "";
        this.resetModal();
      }
    },
  },
  beforeUnmount() {
    // 컴포넌트가 언마운트될 때 body 스크롤 복원
    document.body.style.overflow = "";
  },
  methods: {
    closeModal() {
      this.$emit("close");
    },

    resetModal() {
      this.selectedFile = null;
      this.previewImage = null;
      this.uploading = false;
      // 파일 입력 초기화
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
    },

    triggerFileSelect() {
      if (this.$refs.fileInput) {
        this.$refs.fileInput.click();
      }
    },

    handleFileSelect(event) {
      const file = event.target.files[0];
      if (!file) return;

      // 파일 크기 제한 (5MB)
      if (file.size > 5 * 1024 * 1024) {
        this.$alert("파일 크기는 5MB 이하여야 합니다.");
        return;
      }

      // 이미지 파일인지 확인
      if (!file.type.startsWith("image/")) {
        this.$alert("이미지 파일만 업로드 가능합니다.");
        return;
      }

      this.selectedFile = file;

      // 미리보기 이미지 생성
      const reader = new FileReader();
      reader.onload = (e) => {
        this.previewImage = e.target.result;
      };
      reader.readAsDataURL(file);
    },

    async handleUpload() {
      if (!this.selectedFile) return;

      try {
        this.uploading = true;

        // Storage에 이미지 업로드
        const { downloadURL, fileName } = await uploadVenueImage(
          this.selectedFile,
          this.venueName
        );

        // Firestore에 이미지 정보 저장
        await setVenueImage(this.venueName, fileName);

        // 부모 컴포넌트에 업데이트 알림
        this.$emit("image-updated", downloadURL);

        // 모달 닫기
        this.closeModal();
      } catch (error) {
        console.error("공연장 이미지 업로드 오류:", error);
        this.$alert("공연장 이미지 업로드 중 오류가 발생했습니다.");
      } finally {
        this.uploading = false;
      }
    },

    async handleDelete() {
      if (!this.currentImage) return;

      if (!confirm("공연장 이미지를 삭제하시겠습니까?")) return;

      try {
        this.uploading = true;

        // Storage에서 파일 삭제
        const fileName = this.currentImage.split("/").pop().split("?")[0]; // URL에서 파일명 추출
        await deleteImage("venue", fileName);

        // Firestore에서 이미지 정보 삭제
        await removeVenueImage(this.venueName);

        // 부모 컴포넌트에 삭제 알림
        this.$emit("image-removed");

        // 모달 닫기
        this.closeModal();
      } catch (error) {
        console.error("공연장 이미지 삭제 오류:", error);
        this.$alert("공연장 이미지 삭제 중 오류가 발생했습니다.");
      } finally {
        this.uploading = false;
      }
    },
  },
};
</script>

<style module>
/* 기본적으로 ProfileImageModal과 동일한 스타일 사용 */
.modalOverlay {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 800 !important;
  padding: 20px;
  overflow: hidden;
  overscroll-behavior: none;
  touch-action: none;
}

.modalContent {
  background: white;
  border-radius: 12px;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.modalHeader {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e9ecef;
}

.modalTitle {
  margin: 0;
  font-size: 1.25rem;
  font-weight: bold;
  color: #333;
  font-family: "NanumSquareRoundB", sans-serif;
}

.closeButton {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #666;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.closeButton:hover {
  background: #f8f9fa;
  color: #333;
}

.modalBody {
  padding: 24px;
}

.imagePreviewContainer {
  text-align: center;
  margin-bottom: 24px;
}

.imagePreview {
  width: 200px;
  height: 150px;
  margin: 0 auto 12px;
  border-radius: 12px;
  overflow: hidden;
  border: 3px solid #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
}

.imagePreview button {
  width: 100%;
  height: 100%;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
}

.previewImage {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.noImage {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  color: #999;
  width: 100%;
  height: 100%;
}

.noImage span {
  font-size: 48px;
  font-weight: bold;
}

.imageLabel {
  margin: 0;
  font-size: 1rem;
  font-weight: 500;
  color: #333;
  font-family: "NanumSquareRoundR", sans-serif;
}

.uploadSection {
  text-align: center;
}

.hiddenInput {
  display: none;
}

.uploadHint {
  margin: 0;
  font-size: 0.875rem;
  color: #666;
  font-family: "NanumSquareRoundR", sans-serif;
}

.uploadProgress {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 20px;
  /* background: #f8f9fa; */
  border-radius: 8px;
  margin-bottom: 24px;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #66bdff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.uploadProgress p {
  margin: 0;
  color: #666;
  font-family: "NanumSquareRoundR", sans-serif;
}

.modalFooter {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid #e9ecef;
  background: #f8f9fa;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
}

.deleteButton {
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 10px 20px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "NanumSquareRoundR", sans-serif;
}

.deleteButton:hover:not(:disabled) {
  background: #c82333;
}

.saveButton {
  background: #28a745;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 10px 20px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "NanumSquareRoundR", sans-serif;
}

.saveButton:hover:not(:disabled) {
  background: #218838;
}

.deleteButton:disabled,
.saveButton:disabled {
  background: #ccc;
  cursor: not-allowed;
}

/* 반응형 디자인 */
@media (max-width: 600px) {
  .modalContent {
    margin: 10px;
    max-height: 95vh;
  }

  .modalHeader,
  .modalBody,
  .modalFooter {
    padding: 16px 20px;
  }

  .imagePreview {
    width: 160px;
    height: 120px;
  }

  .noImage span {
    font-size: 36px;
  }

  .modalFooter {
    flex-direction: column;
    gap: 8px;
  }

  .deleteButton,
  .saveButton {
    width: 100%;
    padding: 12px;
    font-size: 1rem;
  }
}
</style>
